import mapValues from 'lodash/mapValues'
import pickBy from 'lodash/pickBy'

import type { ISO8601 } from '@app/constants/Misc'

import { excludeAbortError } from '@app/errors/AbortError'

import { createSelector } from '@app/utils/createSelector'
import { createSortHandler } from '@app/utils/createSortHandler'
import moment from '@app/utils/moment'
import { normalize } from '@app/utils/normalizer'

import type { ApiRegion } from '@app/packages/api/v2/types/entities'
import { mergeApiEntities } from '@app/packages/apiTools/mergeApiEntity'
import { asError } from '@app/packages/asError/asError'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

const regionsSlice = createReduxSlice<RegionsState>('regions')

export const setRegionsSlice = (state: RegionsState) => regionsSlice.set(state)

/** The whole list, with the loading state around it. The only caller is the refresh service. */
export const fetchRegions = () =>
  createThunk(async dispatch => {
    dispatch(regionsSlice.update(state => withState(state, { loading: true, error: null })))
    const { getRegions } = await import('@app/packages/api/v2/actions/regions')
    try {
      const resp = await dispatch(callV2Api(getRegions()))
      dispatch(
        regionsSlice.update(state => {
          const base = state ?? DEFAULT_STATE
          return {
            ...base,
            list: resp.data
              .toSorted(createSortHandler(c => [c.attributes.type === 'world' ? 0 : c.attributes.type === 'country' ? 1 : 2, -Number(c.id)]))
              .map(c => c.id),
            state: { ...base.state, loading: false, loaded: true, loadedAt: moment().format() },
          }
        })
      )
      return resp
    } catch (e) {
      dispatch(regionsSlice.update(state => withState(state, { loading: false, error: excludeAbortError(asError(e)) })))
      throw e
    }
  })

/**
 * Regions ride along with several responses (contacts, schools, a place change). A region without
 * a default place is a stub the api includes for reference, so it must not replace a full one.
 */
export const updateRegionsSlice = (payload: unknown) =>
  regionsSlice.update(state => {
    const base = state ?? DEFAULT_STATE
    const normalized = normalizeSafe(payload).regions
    if (!normalized) return state
    const regions = pickBy(replaceDashes(normalized), r => !!r.relationships.default_place.data || !base.models[r.id])
    return { ...base, models: mergeApiEntities(base.models, regions) }
  })

const withState = (state: RegionsState | undefined, patch: Partial<RegionsState['state']>): RegionsState => {
  const base = state ?? DEFAULT_STATE
  return { ...base, state: { ...base.state, ...patch } }
}

const normalizeSafe = (payload: unknown) => {
  try {
    return normalize(payload as Parameters<typeof normalize>[0]) as { regions?: { [id: string]: ApiRegion } }
  } catch {
    return {}
  }
}

export const regionsStateSelector = createSelector([regionsSlice.selector], state => state?.state ?? DEFAULT_STATE.state)
export const regionsListSelector = createSelector([regionsSlice.selector], state => state?.list ?? DEFAULT_STATE.list)
export const regionsMapSelector = createSelector([regionsSlice.selector], state => state?.models ?? DEFAULT_STATE.models)

type RegionsState = {
  state: {
    loaded: boolean
    loading: boolean
    loadedAt: ISO8601 | null
    error: Error | null
  }
  list: string[]
  models: { [key: string]: ApiRegion }
}

const DEFAULT_STATE: RegionsState = {
  state: {
    loaded: false,
    loading: false,
    loadedAt: null,
    error: null,
  },
  list: [],
  models: {},
}

/**
 * replace dashes with non-breaking hyphens in region name
 * main reason is Rostov-on-Don
 */
const replaceDashes = (regions: { [key: string]: ApiRegion }) => {
  return mapValues(regions, c => ({
    ...c,
    attributes: {
      ...c.attributes,
      name: c.attributes.name.replace(/-/gi, '‑'),
      prepositional_name: c.attributes.prepositional_name.replace(/-/gi, '‑'),
    },
  }))
}
